import { useState, useEffect, useCallback } from 'react'
import { 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'
import { getCumulativeTotalSoldData } from '../../supabase-client'
import DateRangeSelector from './DateRangeSelector'

// Custom tooltip with shadcn styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">
                Cumulative Total
              </span>
            </div>
            <span className="font-medium">
              {entry.value.toFixed(1)}kg
            </span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

export default function CumulativeTotalSoldChart({ dateRange: externalDateRange, loading: externalLoading, compact = false }) {
  const [internalDateRange, setInternalDateRange] = useState('all')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  // Use external date range if provided, otherwise use internal
  const dateRange = externalDateRange || internalDateRange

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const cumulativeData = await getCumulativeTotalSoldData(dateRange)
      setData(cumulativeData)
    } catch (error) {
      console.error('Error loading cumulative total sold data:', error)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    loadData()
  }, [loadData])

  const isLoading = externalLoading !== undefined ? externalLoading : loading
  if (isLoading) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-loading">
          Loading cumulative total sold data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-loading">
          No cumulative total sold data available for the selected period
        </div>
      </div>
    )
  }

  return (
    <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
      {!compact && !externalDateRange && (
        <div className="flex justify-between items-center mb-4">
          <div></div>
          <DateRangeSelector 
            value={dateRange} 
            onValueChange={setInternalDateRange}
            className="w-[140px]"
          />
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid 
            strokeDasharray="none" 
            stroke="var(--border)" 
            strokeWidth="1"
            opacity="0.3"
          />
          
          {/* Minor Y-axis reference lines at 500kg intervals */}
          {(() => {
            if (data.length === 0) return null
            const maxValue = Math.max(...data.map(d => d.cumulativeTotal))
            const maxTick = Math.ceil(maxValue / 1000) * 1000
            const lines = []
            
            for (let i = 500; i < maxTick; i += 1000) {
              lines.push(
                <ReferenceLine 
                  key={`minor-y-${i}`}
                  y={i} 
                  stroke="var(--border)" 
                  strokeWidth="0.5" 
                  opacity="0.15"
                  strokeDasharray="none"
                />
              )
            }
            return lines
          })()}
          
          {/* Minor X-axis reference lines for H2 periods */}
          {data.map((item) => {
            if (!item.isH1) { // H2 periods get minor grid lines
              return (
                <ReferenceLine 
                  key={`minor-x-${item.period}`}
                  x={item.period} 
                  stroke="var(--border)" 
                  strokeWidth="0.5" 
                  opacity="0.15"
                  strokeDasharray="none"
                />
              )
            }
            return null
          })}
          
          <XAxis 
            dataKey="period"
            axisLine={true}
            tickLine={true}
            tick={(props) => {
              const { x, y, payload } = props
              const dataItem = data.find(item => item.period === payload.value)
              
              if (!dataItem) return null
              
              // Only show year labels for H1 (first half of year)
              if (dataItem.isH1) {
                return (
                  <g>
                    {/* Major tick mark */}
                    <line 
                      x1={x} 
                      y1={y - 10} 
                      x2={x} 
                      y2={y} 
                      stroke="var(--muted-foreground)" 
                      strokeWidth={2}
                    />
                    {/* Year label */}
                    <text 
                      x={x} 
                      y={y + 15} 
                      textAnchor="middle" 
                      fill="var(--muted-foreground)"
                      fontSize={12}
                      fontFamily="Geist, -apple-system, BlinkMacSystemFont, sans-serif"
                    >
                      {dataItem.displayYear}
                    </text>
                  </g>
                )
              } else {
                // Minor tick mark for H2
                return (
                  <g>
                    <line 
                      x1={x} 
                      y1={y - 5} 
                      x2={x} 
                      y2={y} 
                      stroke="var(--muted-foreground)" 
                      strokeWidth={1}
                    />
                  </g>
                )
              }
            }}
          />
          <YAxis 
            axisLine={true}
            tickLine={true}
            tick={{ 
              fontSize: 12, 
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{ 
              value: 'Cumulative Volume (kg)', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
            tickCount={Math.ceil(Math.max(...data.map(d => d.cumulativeTotal)) / 1000) + 1}
            domain={[0, 'dataMax']}
            ticks={(() => {
              if (data.length === 0) return [0, 1000, 2000, 3000, 4000, 5000]
              const maxValue = Math.max(...data.map(d => d.cumulativeTotal))
              const maxTick = Math.ceil(maxValue / 1000) * 1000
              const ticks = []
              for (let i = 0; i <= maxTick; i += 1000) {
                ticks.push(i)
              }
              return ticks
            })()}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line 
            type="monotone" 
            dataKey="cumulativeTotal" 
            stroke="#009689" 
            strokeWidth={3}
            dot={{ fill: '#009689', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: '#009689' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}